/** Résumé des épreuves pour une inscription (carte Mes cours). */

const APPROACH_DEADLINE_MS = 48 * 60 * 60 * 1000;

export type InscriptionEvaluationRow = {
  statut: string;
  remainingMs?: number | null;
  session: {
    titre: string;
    statut: string;
    dateFermeture?: string | null;
  };
};

export type EvaluationInscriptionSummary = {
  total: number;
  pending: number;
  approachingDeadline: boolean;
  deadlineLabel: string | null;
};

export const EMPTY_EVALUATION_SUMMARY: EvaluationInscriptionSummary = {
  total: 0,
  pending: 0,
  approachingDeadline: false,
  deadlineLabel: null,
};

function sessionOpen(statut: string) {
  return statut === 'OUVERTE' || statut === 'PUBLIEE';
}

function tentativePending(statut: string) {
  return statut === 'EN_ATTENTE' || statut === 'EN_COURS';
}

function formatDeadlineLabel(titre: string, remainingMs: number) {
  const safeTitle = titre.trim() || 'Épreuve';
  if (remainingMs < 60 * 60 * 1000) {
    return `« ${safeTitle} » — moins d'une heure restante`;
  }
  if (remainingMs < 24 * 60 * 60 * 1000) {
    const hours = Math.ceil(remainingMs / (60 * 60 * 1000));
    return `« ${safeTitle} » — fermeture dans ${hours} h`;
  }
  const days = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
  return `« ${safeTitle} » — avant ${days} jour${days > 1 ? 's' : ''}`;
}

export function summarizeInscriptionEvaluations(
  rows: InscriptionEvaluationRow[],
): EvaluationInscriptionSummary {
  if (!rows.length) return EMPTY_EVALUATION_SUMMARY;

  let pending = 0;
  let nearestMs: number | null = null;
  let nearestTitle = '';

  for (const row of rows) {
    const { session } = row;
    if (sessionOpen(session.statut) && tentativePending(row.statut)) {
      pending += 1;
    }

    let remainingMs: number | null = null;
    if (row.statut === 'EN_COURS' && row.remainingMs != null && row.remainingMs > 0) {
      remainingMs = row.remainingMs;
    } else if (session.dateFermeture && sessionOpen(session.statut) && tentativePending(row.statut)) {
      remainingMs = new Date(session.dateFermeture).getTime() - Date.now();
    }

    if (remainingMs != null && remainingMs > 0 && (nearestMs == null || remainingMs < nearestMs)) {
      nearestMs = remainingMs;
      nearestTitle = session.titre;
    }
  }

  const approachingDeadline = nearestMs != null && nearestMs > 0 && nearestMs <= APPROACH_DEADLINE_MS;

  return {
    total: rows.length,
    pending,
    approachingDeadline,
    deadlineLabel:
      approachingDeadline && nearestMs != null
        ? formatDeadlineLabel(nearestTitle, nearestMs)
        : null,
  };
}

export function evaluationBadgeLabel(summary: EvaluationInscriptionSummary) {
  if (summary.total <= 0) return null;
  if (summary.pending > 0) {
    return summary.pending === 1 ? '1 évaluation à faire' : `${summary.pending} évaluations à faire`;
  }
  return summary.total === 1 ? '1 évaluation' : `${summary.total} évaluations`;
}
